/**
 * Generic polling helper with exponential backoff.
 *
 * - Starts at 1 s, multiplies by 1.5 each iteration, caps at 5 s.
 * - Gives up after 10 minutes with a user-facing error message.
 * - Checks signal.cancelled at the top of each loop; set it to true
 *   from a useEffect cleanup to abort polling on component unmount.
 */
export async function pollWithBackoff<T>(
  fetcher: () => Promise<T>,
  isDone: (result: T) => boolean,
  onUpdate: (result: T) => void,
  signal: { cancelled: boolean },
): Promise<T> {
  const BUDGET_MS = 10 * 60 * 1000
  const CAP_MS = 5_000
  const MULTIPLIER = 1.5
  const deadline = Date.now() + BUDGET_MS
  let delayMs = 1_000

  for (;;) {
    if (signal.cancelled) throw new Error('cancelled')

    const result = await fetcher()
    onUpdate(result)
    if (isDone(result)) return result

    if (Date.now() + delayMs > deadline) {
      throw new Error(
        'Backup did not complete after 10 minutes — check the audit log or contact your administrator.',
      )
    }

    await new Promise<void>((resolve) => setTimeout(resolve, delayMs))
    delayMs = Math.min(delayMs * MULTIPLIER, CAP_MS)
  }
}
